import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, Quote, Play, X } from "lucide-react";
import { AnimatePresence } from "framer-motion";
import axios from "axios";
import { useLanguage } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.6, ease: "easeOut" } }),
};

export default function TestimonialsPage() {
  const { t } = useLanguage();
  const [testimonials, setTestimonials] = useState([]);
  const [videoUrl, setVideoUrl] = useState(null);

  useEffect(() => {
    axios.get(`${API}/testimonials`).then((res) => setTestimonials(res.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="testimonials-page" className="pt-20">
      <section className="py-24 md:py-32 bg-luxury-black">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial="hidden" animate="visible" className="max-w-3xl mb-16">
            <motion.p variants={fadeUp} custom={0} className="text-xs font-body font-medium uppercase tracking-[0.3em] text-gold mb-6">Testimonials</motion.p>
            <motion.h1 variants={fadeUp} custom={1} className="font-heading text-5xl sm:text-6xl font-light text-white leading-none mb-6">
              {t("testimonials.title")}<br /><span className="gold-gradient-text">{t("testimonials.highlight")}</span>
            </motion.h1>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((item, i) => (
              <motion.div
                key={item.id}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={i}
                data-testid={`testimonial-${item.id}`}
                className="glass-card p-10 relative"
              >
                <Quote size={32} className="text-gold/20 absolute top-6 right-6" />
                <div className="flex gap-1 mb-5">
                  {Array.from({ length: item.rating }).map((_, j) => (
                    <Star key={j} size={14} className="text-gold fill-gold" />
                  ))}
                </div>
                <p className="text-white/70 font-body font-light text-base leading-relaxed mb-8 italic">"{item.quote}"</p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-body font-medium text-sm">{item.name}</p>
                    <p className="text-white/40 font-body text-xs">{item.role}</p>
                  </div>
                  {item.video_url && (
                    <button
                      onClick={() => setVideoUrl(item.video_url)}
                      data-testid={`testimonial-video-${item.id}`}
                      className="flex items-center gap-2 bg-gold/10 text-gold px-4 py-2 text-xs font-body hover:bg-gold/20 transition-colors"
                    >
                      <Play size={14} /> {t("testimonials.watchVideo")}
                    </button>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Video Modal */}
      <AnimatePresence>
        {videoUrl && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-6"
            onClick={() => setVideoUrl(null)}
            data-testid="video-modal"
          >
            <button onClick={() => setVideoUrl(null)} className="absolute top-6 right-6 text-white/60 hover:text-white z-10"><X size={28} /></button>
            <div onClick={e => e.stopPropagation()} className="w-full max-w-4xl aspect-video">
              <iframe src={videoUrl} className="w-full h-full" allow="accelerometer; autoplay; encrypted-media; gyroscope" allowFullScreen title="Testimonial Video" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
